/**
 * INFO: The use case for this component is to display a small chunk of data which needs to be retrieve  
 * at once and, needs to be filtered out.
 * 
 * EXCEPTION: In case we need to call a backend API every time the user types in something, 
 * is better to use a regular mat-autocomplete implementation.
 * 
 */

import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { untilDestroyed, UntilDestroy } from '@ngneat/until-destroy';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { DataObject } from '../../../models/data-objects.models';

@UntilDestroy()
@Component({
  selector: 'iv-autocomplete',
  templateUrl: './autocomplete.component.html',
  styleUrls: ['./autocomplete.component.scss'],
})
export class AutocompleteComponent implements OnInit, OnChanges {

  @Input() fieldName: string = '';
  @Input() fieldLabel: string = '';
  @Input() fieldOptLabel: string = '';
  @Input() fieldId: string = '';
  @Input() isRequired: boolean = false;
  @Input() fieldValue: string = ''; // What's stored in the DB
  @Input() fieldObjectValue: DataObject = {};
  @Input() fieldListOptions = [];
  @Input() saveOnChanges: boolean = false;
  @Input() isDisabled: boolean = false;
  @Input() noEntriesFoundLabel: string = 'No entries found';
  @Input() displayTooltip: boolean = false;
  @Input() isAddressAutocomplete: boolean = false;
  @Input() returnObject: boolean = false;
  @Input() debounceTime: number = 500;
  @Input() placeHolderText: string = '';
  /**
  * INFO: For 'fieldKeys' we can pass different combinations based on the fieldListOptions array
  * and its keys (API Response), so we can correctly do the mapping, by default we use label and value, see below examples:
  * {'label': 'name', 'value': 'code'};
  * {'label': 'xyz', 'value': 'asd'};
  
  * We can also use a CUSTOM key to display the value in the dropdown, for example:
  * {'label': 'name', 'value': 'code', 'custom': 'name+code'}; 
  * The dropdown will display the option as 'name (code)' and the value will be 'code'.

  * We can also use a custom SEPARATOR, for example:
  * {'label': 'name', 'value': 'code', 'custom': 'name+code', 'separator': ' - '}; 
  * The dropdown will display the option as 'name - code' and the value will be 'code'.

  * We can also EXTEND THE SEARCH on other keys, for example:
  * {'label': 'name', 'value': 'code', 'extendSearchOn': 'description+id'}; 
  * The search will be done on name, code, description and id.
  
  * How do we call the component?
  * <iv-autocomplete [fieldKeys]="{'label': 'name', 'value': 'code'}"></iv-autocomplete>
  */
  @Input() fieldKeys: { [key: string]: string } = { 'label': 'label', 'value': 'value', 'custom': '', 'separator': '', 'extendSearchOn': '' };
  @Output() saveField: EventEmitter<any>;

  displayedValue: string = ''; // What's displayed in the input field
  textTyped$: Subject<string> = new Subject<string>();
  filteredOptions = [];

  constructor() {
    this.saveField = new EventEmitter();
  }

  ngOnInit(): void {
    this.textTypedSubscription();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Reset the filteredOptions based on the fieldListOptions changes
    if (changes && changes.fieldListOptions?.currentValue) {
      this.filteredOptions = changes.fieldListOptions?.currentValue;
      this.getDisplayedValue();
    }
    if (changes && changes.fieldValue?.currentValue) {
      this.getDisplayedValue();
    }
  }

  getDisplayedValue(): void {
    if (this.fieldListOptions?.length > 0) {
      const valueToMatch = this.isAddressAutocomplete && this.fieldObjectValue ? this.fieldObjectValue?.address_seq_num : this.fieldValue;
      const selectedOption = this.fieldListOptions.filter((option: any) => {
        const optionToMatchWith = option[this.fieldKeys.value];
        return `${optionToMatchWith}` === `${valueToMatch}`;
      });
      if (selectedOption.length === 1) {
        this.displayedValue = this.displayFn(selectedOption[0]);
      } else {
        this.displayedValue = '';  
      }
    } else {
      this.displayedValue = '';
    }
  }

  displayFn(option: DataObject): string {
    if (!this.fieldKeys.separator) {
      this.fieldKeys.separator = ' | ';
    }
    if (this.fieldKeys.custom) {
      const customKeys = this.fieldKeys.custom.split('+');
      let customOption: string[] = [];
      customKeys.forEach((key: string) => {
        if (this.fieldKeys[key]) {
          customOption.push(option[this.fieldKeys[key]]);
        } else if (option[key.toLowerCase()] || option[key.toUpperCase()]) {
          customOption.push(option[key.toLowerCase()] || option[key.toUpperCase()]);
        }
      });
      return customOption
        .filter((value: string) => value && value !== ' ')
        .join(this.fieldKeys.separator);
    }
    return option[this.fieldKeys.label];
  }

  optionSelected(event: any) {
    if (!event.source.selected) {
      return;
    }
    // INFO: In case we want to return the object instead of the value
    if (this.returnObject) {
      this.saveField.emit(event.source.value);
      return;
    }
    this.saveField.emit(event.source.value[this.fieldKeys.value]);
  }

  // INFO: In case we face some issues during the filtering, please double-check fieldKeys to
  // ensure we're using the correct mapping based on the fieldListOptions.
  textTypedSubscription() {
    // Initialized filter options to display the dropdown options on focus
    this.filteredOptions = this.fieldListOptions || [];
    this.textTyped$.pipe(
      untilDestroyed(this),
      debounceTime(this.debounceTime),
      distinctUntilChanged()
    ).subscribe((fieldInput: string) => {
      if (fieldInput) {
        this.filteredOptions = this.fieldListOptions.filter((field: any) => {
          let optionValue = field[this.fieldKeys.value] || '';
          const optionLabel = field[this.fieldKeys.label] || '';
          if (typeof optionValue === 'number') {
            optionValue = optionValue.toString();
          }

          // Check primary fields first (label and value)
          const matchesPrimary = optionValue.toLowerCase().includes(fieldInput.toLowerCase()) ||
            optionLabel.toLowerCase().includes(fieldInput.toLowerCase());

          // Check extended search keys if provided
          const extendedKeys = this.getExtendedSearchKeys();
          const matchesExtended = extendedKeys.some(key => {
            const extendedOptionValue = field[key] ? field[key].toString().toLowerCase() : '';
            return extendedOptionValue.includes(fieldInput.toLowerCase());
          });

          return matchesPrimary || matchesExtended;
        });
        if (this.saveOnChanges) {
          this.saveField.emit(fieldInput);
        }
      } else {
        this.filteredOptions = this.fieldListOptions;
      }
    });
  }

  getExtendedSearchKeys(): string[] {
    if (this.fieldKeys.extendSearchOn) {
      return this.fieldKeys.extendSearchOn.split('+');
    }
    return [];
  }

}
